import { ExportOutlined } from '@ant-design/icons'
import { TimeSlot, UserHomeFeedItem } from '@models/Availability'
import { AppRestriction } from '@models/Venue'
import {
  Alert,
  Card,
  Collapse,
  Empty,
  Flex,
  Skeleton,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { FC } from 'react'
import { Link } from 'react-router-dom'
import { AvailabilityViewerSeatingArea } from './SeatingArea'

const { Text, Title } = Typography

// At-a-glance status shown in the (collapsed) panel header so an admin can scan the whole feed
// without expanding every venue.
const StatusTag: FC<{ item: UserHomeFeedItem }> = ({ item }) => {
  const availability = item.availability

  if (!availability) return <Tag color="warning">No availability</Tag>
  if (availability.isClosed) return <Tag>Closed</Tag>

  const count = availability.times?.length ?? 0
  if (!count) return <Tag>No slots</Tag>

  return (
    <Tag color="success">
      {count} slot{count > 1 ? 's' : ''}
    </Tag>
  )
}

// Restriction badge in the (collapsed) panel header, driven by the venue's app-restriction mode.
// `everyone` (or unset) shows nothing.
const RestrictionTag: FC<{ appRestriction?: AppRestriction | null }> = ({
  appRestriction,
}) => {
  if (appRestriction === 'only_members')
    return <Tag color="gold">Member only</Tag>
  if (appRestriction === 'only_non_members')
    return <Tag color="blue">Non-members only</Tag>
  return null
}

/**
 * The availability detail for a single venue, computed as the selected user — rendered inside its
 * collapsible panel.
 */
const VenueAvailabilityBody: FC<{ item: UserHomeFeedItem }> = ({ item }) => {
  const { availability } = item

  const seatingAreaNames = (() => {
    const set = new Set<string>()
    availability?.times?.forEach((time) =>
      time.seatingAreas?.forEach((area) => set.add(area.name))
    )
    return Array.from(set).sort()
  })()

  // A null availability means the venue has no bookable config / the Sevenrooms call returned
  // nothing — not that it is closed (a closed venue is a non-null availability with isClosed).
  if (!availability) {
    return (
      <Alert
        type="warning"
        showIcon
        title="No availability could be fetched. Check the venue's booking configuration."
      />
    )
  }

  if (availability.isClosed) {
    return <Alert type="info" showIcon title="Closed on the selected date." />
  }

  if (!availability.times?.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No bookable slots for this user."
      />
    )
  }

  const columns: TableColumnsType<TimeSlot> = [
    {
      title: 'Time',
      key: 'time',
      fixed: 'left',
      width: 90,
      render: (_, record) => <Text>{dayjs(record.from).format('HH:mm')}</Text>,
    },
    ...seatingAreaNames.map((areaName) => ({
      title: areaName,
      key: `seatingArea-${areaName}`,
      width: 200,
      render: (_: unknown, record: TimeSlot) => {
        const area = record.seatingAreas?.find((a) => a.name === areaName)

        return area ? (
          <AvailabilityViewerSeatingArea area={area} />
        ) : (
          <Flex align="center" justify="center">
            <Tag color="red">Unavailable</Tag>
          </Flex>
        )
      },
    })),
  ]

  return (
    <Table
      bordered
      size="small"
      rowKey="fromIso8601"
      columns={columns}
      pagination={false}
      dataSource={availability.times}
      scroll={{ x: true }}
    />
  )
}

const UserHomeFeed: FC<{
  items?: UserHomeFeedItem[]
  isLoading: boolean
}> = ({ items, isLoading }) => {
  if (isLoading) {
    return <Skeleton active loading />
  }

  if (!items?.length) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No venues are visible to this user."
        />
      </Card>
    )
  }

  const collapseItems = items.map((item) => ({
    key: String(item.venue.id),
    label: (
      <Flex align="center" gap="small" wrap>
        <Text strong>{item.venue.name}</Text>
        <RestrictionTag appRestriction={item.venue.appRestriction} />
        <StatusTag item={item} />
      </Flex>
    ),
    extra: (
      <Link
        to={`/venues/${item.venue.slug}/booking`}
        target="_blank"
        // Don't toggle the panel when following the link.
        onClick={(e) => e.stopPropagation()}
      >
        <Space size={4}>
          Booking config
          <ExportOutlined />
        </Space>
      </Link>
    ),
    children: <VenueAvailabilityBody item={item} />,
  }))

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Title level={5} style={{ margin: 0 }}>
        {items.length} venue{items.length > 1 ? 's' : ''} visible to this user
      </Title>
      {/* Collapsed by default — the header status tags let you scan the whole feed without
          opening every venue (the list can be long). */}
      <Collapse items={collapseItems} expandIconPosition="start" />
    </Space>
  )
}

export default UserHomeFeed
